import { ArrowRight, Plus, User, UserRound, Circle, X } from "lucide-react";

const initials = (name = "") => name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

/* ─────────────────────────────────────────────
   STYLES — premium blue design system
   (shared palette/type with the rest of the app suite)
───────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,600;12..96,700;12..96,800&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500;600&display=swap');

  .wk-root,.wk-root *{box-sizing:border-box}
  .wk-root{
    --card:#FFFFFF; --card-2:#F7F9FD;
    --ink:#101828; --ink-2:#344055; --muted:#66738A; --faint:#98A2B8;
    --line:#E4E9F5; --line-2:#D3DCF3;
    --blue:#3054E0; --blue-deep:#152C7A; --blue-tint:#EAEFFD;
    --amber-tint:#FBF1DD;
    --font-body:'Inter',sans-serif; --font-disp:'Bricolage Grotesque',var(--font-body); --font-mono:'JetBrains Mono',monospace;
    --ease:cubic-bezier(.22,1,.36,1);
    display:flex;flex-direction:column;width:min(460px,calc(100vw - 32px));max-height:min(720px,calc(100dvh - 32px));
    background:var(--card);border-radius:26px;box-shadow:0 34px 76px rgba(16,24,40,.24);
    overflow:hidden;color:var(--ink);font-family:var(--font-body);animation:wk-enter .28s var(--ease);margin:0 auto
  }
  @keyframes wk-enter{from{opacity:0;transform:translateY(14px) scale(.97)}to{opacity:1;transform:none}}
  @keyframes wk-fade{from{opacity:0}to{opacity:1}}

  .wk-band{position:relative;flex:none;padding:26px 24px 40px;background:radial-gradient(ellipse 120% 160% at 10% -20%,rgba(255,255,255,.18),transparent 55%),
    linear-gradient(135deg,var(--blue) 0%,var(--blue-deep) 130%);color:#fff}
  .wk-close{position:absolute;top:18px;right:18px;width:32px;height:32px;border-radius:10px;border:1px solid rgba(255,255,255,.3);
    background:rgba(255,255,255,.12);color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all .15s var(--ease)}
  .wk-close:hover{background:rgba(255,255,255,.22)}
  .wk-title{font-family:var(--font-disp);font-size:22px;font-weight:700;letter-spacing:-.4px;margin:0}
  .wk-subtitle{font-size:12.5px;color:#C7D4F9;margin-top:5px}
  .wk-badge{position:absolute;left:24px;bottom:-26px;width:52px;height:52px;border-radius:16px;background:var(--card);
    box-shadow:0 10px 24px rgba(16,24,40,.16);display:flex;align-items:center;justify-content:center;color:var(--blue)}

  .wk-body{flex:1 1 auto;min-height:0;overflow-y:auto;overscroll-behavior:contain;padding:38px 24px 24px;display:flex;flex-direction:column;gap:18px}

  .wk-fam-row{display:flex;align-items:center;justify-content:space-between;gap:10px}
  .wk-fam-label{font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.05em}
  .wk-new-link{font-size:11.5px;font-weight:700;color:var(--blue);background:none;border:none;cursor:pointer;display:flex;align-items:center;gap:4px;padding:0}
  .wk-fam-scroll{display:flex;gap:12px;overflow-x:auto;padding-bottom:2px;margin-top:10px}
  .wk-avatar-chip{display:flex;flex-direction:column;align-items:center;gap:6px;cursor:pointer;flex-shrink:0;background:none;border:none;padding:2px}
  .wk-avatar{width:46px;height:46px;border-radius:50%;background:var(--blue-tint);color:var(--blue-deep);display:flex;align-items:center;
    justify-content:center;font-family:var(--font-disp);font-weight:700;font-size:15px;border:2px solid transparent;transition:all .18s var(--ease)}
  .wk-avatar-chip.active .wk-avatar{border-color:var(--blue);background:linear-gradient(135deg,var(--blue),var(--blue-deep));color:#fff;
    box-shadow:0 6px 16px rgba(48,84,224,.3);transform:translateY(-2px)}
  .wk-avatar-chip:active .wk-avatar{transform:scale(.92)}
  .wk-avatar-name{font-size:10px;color:var(--muted);font-weight:600;max-width:52px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .wk-avatar-chip.active .wk-avatar-name{color:var(--blue-deep)}

  .wk-banner{display:flex;align-items:center;gap:10px;padding:11px 13px;border-radius:13px;background:var(--amber-tint);
    color:#8A5A0A;font-size:12px;animation:wk-fade .25s ease}
  .wk-banner-ic{width:28px;height:28px;border-radius:9px;background:#F6E4B8;display:flex;align-items:center;justify-content:center;flex-shrink:0}
  .wk-banner b{font-family:var(--font-mono)}

  .wk-float{position:relative}
  .wk-float input{width:100%;height:54px;padding:22px 15px 7px;border:1.5px solid var(--line-2);border-radius:14px;
    background:var(--card-2);color:var(--ink);font:inherit;font-size:14.5px;outline:none;transition:all .18s var(--ease)}
  .wk-float label{position:absolute;left:16px;top:17px;font-size:14px;color:var(--faint);pointer-events:none;transition:all .16s var(--ease);transform-origin:left top}
  .wk-float input:focus{border-color:var(--blue);background:var(--card);box-shadow:0 0 0 4px rgba(48,84,224,.1)}
  .wk-float input:focus ~ label,.wk-float input:not(:placeholder-shown) ~ label{transform:translateY(-9px) scale(.74);color:var(--blue)}
  .wk-float.with-prefix input{padding-left:50px}
  .wk-prefix{position:absolute;left:16px;top:50%;transform:translateY(-1px);font-family:var(--font-mono);font-weight:700;
    color:var(--blue-deep);font-size:14px;z-index:2;pointer-events:none}
  .wk-float.with-prefix label{left:50px}
  .wk-float.with-prefix input:focus ~ label,.wk-float.with-prefix input:not(:placeholder-shown) ~ label{transform:translateY(-9px) scale(.74) translateX(-34px)}

  .wk-row2{display:grid;grid-template-columns:minmax(0,.8fr) minmax(0,1.2fr);align-items:stretch;gap:12px}
  .wk-gender{display:flex;min-width:0;gap:8px;height:54px}
  .wk-gbtn{flex:1;border:1.5px solid var(--line-2);border-radius:14px;background:var(--card-2);color:var(--muted);cursor:pointer;
    display:flex;min-width:0;padding:4px 2px;flex-direction:column;align-items:center;justify-content:center;gap:2px;font-size:10.5px;font-weight:700;line-height:1.15;text-align:center;transition:all .16s var(--ease)}
  .wk-gbtn.active{border-color:var(--blue);background:linear-gradient(135deg,var(--blue-tint),var(--card));color:var(--blue-deep);box-shadow:0 0 0 3px rgba(48,84,224,.08) inset}
  .wk-gbtn:active{transform:scale(.95)}

  .wk-actions{display:flex;min-width:0;gap:10px;margin-top:4px}
  .wk-cancel{flex:0 0 auto;min-width:100px;height:52px;padding:0 20px;border-radius:14px;border:1.5px solid var(--line-2);background:var(--card);
    color:var(--ink-2);font:inherit;font-size:13.5px;font-weight:700;cursor:pointer;transition:all .18s var(--ease)}
  .wk-cancel:hover{background:var(--card-2)}
  .wk-submit{flex:1;min-width:0;position:relative;overflow:hidden;height:52px;border-radius:14px;border:none;
    background:linear-gradient(135deg,var(--blue),var(--blue-deep));color:#fff;font:inherit;font-size:14px;font-weight:700;cursor:pointer;
    display:flex;align-items:center;justify-content:center;gap:9px;box-shadow:0 14px 28px rgba(48,84,224,.32);transition:all .18s var(--ease)}
  .wk-submit::after{content:'';position:absolute;inset:0;background:linear-gradient(115deg,transparent 30%,rgba(255,255,255,.28) 50%,transparent 70%);
    background-size:220% 100%;background-position:150% 0;transition:background-position .6s var(--ease)}
  .wk-submit:hover::after{background-position:-50% 0}
  .wk-submit:hover{transform:translateY(-1px);box-shadow:0 18px 34px rgba(48,84,224,.4)}

  .wk-close:focus-visible,.wk-new-link:focus-visible,.wk-avatar-chip:focus-visible,.wk-gbtn:focus-visible,.wk-cancel:focus-visible,.wk-submit:focus-visible{
    outline:3px solid rgba(48,84,224,.42);outline-offset:2px
  }
  @media(max-width:480px){
    .wk-root{width:calc(100vw - 24px);max-height:calc(100dvh - 24px);border-radius:22px}
    .wk-band{padding:22px 20px 36px}
    .wk-close{top:14px;right:14px}
    .wk-body{padding:34px 18px 20px;gap:16px}
    .wk-row2{grid-template-columns:minmax(0,.7fr) minmax(0,1.3fr);gap:8px}
    .wk-gender{gap:5px}
    .wk-actions{gap:8px}
    .wk-cancel{min-width:0;padding:0 14px}
    .wk-submit{padding:0 12px}
  }
  @media(max-width:360px){
    .wk-row2{grid-template-columns:minmax(0,1fr)}
    .wk-gender{height:52px}
    .wk-actions{flex-direction:column-reverse}
    .wk-cancel{width:100%;height:46px}
    .wk-submit{width:100%;height:48px}
  }
  @media(max-height:620px){
    .wk-root{max-height:calc(100dvh - 16px);border-radius:18px}
    .wk-band{padding-top:18px;padding-bottom:32px}
    .wk-title{font-size:20px}
    .wk-body{gap:12px;padding-top:30px;padding-bottom:16px}
    .wk-float input,.wk-gender{height:48px}
    .wk-actions{margin-top:0}
    .wk-cancel,.wk-submit{height:46px}
  }
  @media(prefers-reduced-motion:reduce){.wk-root,.wk-banner{animation:none!important}}
`;

export default function WalkInModal({
  onClose, onBasicSubmit, walkInForm, setWalkInForm, previousBooking, accessCode,
  familyMembers, isNewPatient, setIsNewPatient,
}) {
  const honorific = (() => {
    const raw = String(walkInForm.gender || "").trim().toLowerCase();
    if (["m", "male"].includes(raw)) return "Mr.";
    if (["f", "female"].includes(raw)) return "Ms/Mrs";
    return "";
  })();

  const selectMember = (m) => {
    setIsNewPatient(false);
    setWalkInForm((f) => ({ ...f, patientName: m.patientName || "", age: m.age || "", gender: m.gender || f.gender }));
  };
  const startNewMember = () => {
    setIsNewPatient(true);
    setWalkInForm((f) => ({ ...f, patientName: "", age: "", gender: "MALE" }));
  };

  return (
    <div className="wk-root" role="dialog" aria-modal="true" aria-labelledby="wk-title">
      <style>{CSS}</style>

      <div className="wk-band">
        <button type="button" className="wk-close" onClick={onClose} aria-label="Close">
          <X size={15} strokeWidth={2.5} />
        </button>
        <div className="wk-title" id="wk-title">Add walk-in</div>
        <div className="wk-subtitle">Register a patient without an appointment</div>
        <div className="wk-badge"><UserRound size={22} strokeWidth={2} /></div>
      </div>

      <div className="wk-body">
        {familyMembers?.length > 0 && (
          <div>
            <div className="wk-fam-row">
              <span className="wk-fam-label">Family members</span>
              <button type="button" className="wk-new-link" onClick={startNewMember}>
                <Plus size={12} strokeWidth={2.5} /> New patient
              </button>
            </div>
            <div className="wk-fam-scroll">
              {familyMembers.map((m, idx) => {
                const active = walkInForm.patientName === m.patientName && !isNewPatient;
                return (
                  <button type="button" key={`${m.patientName || "member"}-${idx}`} className={`wk-avatar-chip${active ? " active" : ""}`} onClick={() => selectMember(m)}>
                    <span className="wk-avatar">{initials(m.patientName || "?")}</span>
                    <span className="wk-avatar-name">{(m.patientName || "").split(" ")[0]}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {previousBooking && (
          <div className="wk-banner">
            <span className="wk-banner-ic"><Circle size={10} strokeWidth={3} /></span>
            <span>Previous booking found — access code <b>{accessCode || previousBooking.accessCode || "-"}</b></span>
          </div>
        )}

        <form onSubmit={onBasicSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="wk-float with-prefix">
            <span className="wk-prefix">+91</span>
            <input
              id="wk-phone" required type="tel" maxLength={10} placeholder=" " inputMode="numeric"
              value={walkInForm.phone}
              onChange={(e) => setWalkInForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, "").slice(0, 10) }))}
            />
            <label htmlFor="wk-phone">Phone number</label>
          </div>

          <div className="wk-float">
            <input
              id="wk-name" required placeholder=" "
              value={walkInForm.patientName}
              onChange={(e) => setWalkInForm((f) => ({ ...f, patientName: e.target.value }))}
            />
            <label htmlFor="wk-name">{honorific ? `${honorific} — Patient full name` : "Patient full name"}</label>
          </div>

          <div className="wk-row2">
            <div className="wk-float">
              <input
                id="wk-age" required type="number" min="0" max="120" placeholder=" "
                value={walkInForm.age}
                onChange={(e) => setWalkInForm((f) => ({ ...f, age: e.target.value }))}
              />
              <label htmlFor="wk-age">Age</label>
            </div>
            <div className="wk-gender">
              {[["MALE", "Male", User], ["FEMALE", "Female", UserRound], ["OTHER", "Other", Circle]].map(([value, label, Icon]) => (
                <button
                  type="button" key={value}
                  className={`wk-gbtn${walkInForm.gender === value ? " active" : ""}`}
                  onClick={() => setWalkInForm((f) => ({ ...f, gender: value }))}
                >
                  <Icon size={16} strokeWidth={2} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="wk-actions">
            <button type="button" className="wk-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="wk-submit">
              Continue <ArrowRight size={15} strokeWidth={2.3} />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}